import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Pagination,
  Grid,
  Link,
  Container,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  MoreHoriz as MoreHorizIcon,
} from '@mui/icons-material';
import MainHeader from '../shared/organism/MainHeader';
import infoIcon from '@meprism/shared/src/assets/icons/Info.svg';
import { CorporateBillingSelector, getBillingOverview } from '../../redux/CorporateBilling/CorporateBillingSlice';
import { useAppDispatch } from '../../redux/storeExports';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'invoice-generated':
      return '#f7d327';
    case 'paid':
      return '#52c173';
    case 'expired':
      return '#ef6762';
    default:
      return '#6C757D';
  }
};

const getStatusBackground = (status: string) => {
  switch (status) {
    case 'invoice-generated':
      return '#fffbe9';
    case 'paid':
      return '#ecf8f0';
    case 'expired':
      return '#feeeee';
    default:
      return '#F8F9FA';
  }
};

const statusLabels: Record<string, string> = {
  'invoice-generated': 'Pending',
  'paid': 'Paid',
  'expired': 'Expired',
};

export default function BillingView() {
  const dispatch = useAppDispatch()
  const billingOverview = CorporateBillingSelector()
  useEffect(() => {
      dispatch(getBillingOverview())
    }, [dispatch]
  )
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLg = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isXl = useMediaQuery(theme.breakpoints.up("lg"));

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

 const handleProceedToPay = (invoice_url: string | undefined) => {
  if (invoice_url) {
    window.open(invoice_url, '_blank');
  } else {
    console.warn('No invoice URL available');
  }
};

  if (!billingOverview) {
    return <>
    </>
  }

 function formatOneMonthLater(input: Date): string {
  const date = new Date(input);
  date.setMonth(date.getMonth() + 1);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
function truncateEmail(email: string): string {
  if (!email) return "";
  const [localPart, domain] = email.split("@");
  const maxLocalLength = isSm ? 11 : isMd ? 13 : isLg? 10 : isXl? 14 : 14;

  if (localPart.length <= maxLocalLength) return email;

  return `${localPart.slice(0, maxLocalLength)}...@${domain}`;
}
  const paginatedInvoices = billingOverview.billing_info.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );
  const totalPages = Math.ceil(billingOverview.billing_info.length / rowsPerPage);

  const tableHeaders = ["Invoice", "Date", "Price", "Status", "Download"];

  const isNextInvoice = billingOverview.billing_info?.find(
  (inv) => inv.payment_status === 'invoice-generated'
);
  return (
    <Container fixed maxWidth={'lg'} sx={{ mb: 16, mt: '12px' }}>
      {/* Header */}
      <MainHeader title="Billing Overview" />

      {/* Information Cards */}
      <Grid container columnSpacing={{md:2, lg:5}} rowSpacing={2} sx={{ mt: 0.7, mb: 4 }}>
        {/* Company Information Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            height: {sm: '235px', md:'210px', lg:'235px'},
            borderRadius: '20px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            backgroundColor: '#FFFFFF',
            border: '1px solid #F0F0F0',
            p: '1px',
          }}>
            <CardContent sx={{ p: {sm:3, md:2, lg:3} }}>
              <Chip
                label="Enterprise Plan"
                sx={{
                  background: 'linear-gradient(95.57deg, #1A3493 17.96%, #2F4EC4 47.84%, #4368F5 82.04%)',
                  color: '#fff',
                  mb: 5,
                  p: 1,
                  borderRadius: '20px',
                  fontSize: {sm: 18, md:17, lg:18},
                  fontWeight: 700,
                  fontFamily: 'InterBold',
                  height: {sm: '44px', md:'40px', lg:'44px'},
                  minWidth: 'fit-content',
                  whiteSpace: 'nowrap',
                  width: 'auto',
                  '& .MuiChip-label': {
                    px: 2
                  }
                }}
              />
              <Typography variant="h4" sx={{ 
                fontWeight: 600, 
                mb: 4,
                pl: '3px',
                color: '#000',
                fontSize: {sm: 24, md:22, lg:24},
                fontFamily: 'InterRegular',
              }}>
                {billingOverview.company_info.company_name}
              </Typography>
              
              <Box
                sx={{
                  border: '0.7px solid #E9ECEF',
                  borderRadius: '10px',
                  width: {xs:'250px', sm:'310px', md:'220px', lg:'310px'},
                  padding: {xs: '6px 8px', sm:'12px 16px', md:'6px 8px', lg:'12px 16px'},
                  display: 'inline-block'
                }}
              >
                <Typography variant="body3" sx={{ 
                  color: '#9aa1a7',
                  fontFamily: 'InterRegular',
                  fontWeight: 300,
                  fontSize: {sm: 16, md:13, lg:16},
                  }}>
                  {truncateEmail(billingOverview.company_info.email)}
                </Typography>                                                       
                
             <Box sx={{ display: 'flex', gap: {sm:'6px',md:'2px',lg:'6px'}, alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#9aa1a7',
                    fontFamily: 'InterRegular',
                    fontWeight: 300,
                    fontSize: {sm: 16, md:13, lg:16},
                  }}
                >
                  Subscription Status:
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Box
                    sx={{
                      color: '#00D83D',
                      fontWeight: 500,
                      fontFamily: 'InterRegular',
                      padding: '4px 0 4px 4px',
                      borderRadius: '12px',
                      fontSize: {sm: 16, md:14, lg:16},
                    }}
                  >
                    {billingOverview.company_info.active ? 'Active' : 'Inactive'}
                  </Box>

                  <Tooltip
                    title={
                      <Box sx={{ 
                        fontSize: 12, 
                        fontFamily: 'InterRegular', 
                        color: '#fff',
                        p: 1,
                        maxWidth: 220,
                        textAlign: 'left'
                      }}>
                        If you would like to terminate your Priwall Subscription, please contact us at {' '}
                        <Link 
                          href="mailto:support@meprism.com"
                          sx={{ color: '#4A90E2', fontWeight: 500, textDecoration: 'none' }}
                        >
                          support@meprism.com
                        </Link>
                      </Box>
                    }
                    placement="bottom"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: '#555555',
                          borderRadius: '8px',
                          boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                        }
                      },
                      arrow: {
                        sx: {
                          color: '#2F2F2F',
                        }
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={infoIcon}
                      alt="Info"
                      sx={{
                        width: 16,
                        height: 16,
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                </Box>
              </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Next Invoice Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: {sm:'235px', md:'210px', lg:'235px'},
            borderRadius: '20px',
            fontColor: '#000',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            backgroundColor: '#FFFFFF',
            border: '1px solid #F0F0F0'
          }}>
            <CardContent sx={{ p: {sm:3, md:2.5, lg:3} }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 700, 
                mb: 3,
                color: '#000',
                fontSize: '18px',
                fontFamily: 'InterMedium',
              }}>
                Next Invoice
              </Typography>
              
              
                {isNextInvoice? (
                <Box sx={{ 
                    border: '0.7px solid #E9ECEF',
                    borderRadius: '10px',
                    width: {xs:'100%', md:'216px', lg:'340px'},
                    height: {sm:'75px', md:'65px', lg:'75px'},
                    padding: '12px 16px',
                    display: 'inline-block',
                    mb: {sm:6, md:5, lg:7}
                  }}>
                {/* Row 1: Total Amount + Price */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <Typography variant="body2" sx={{ 
                    color: '#000',
                    fontSize: {sm: '20px', md:'14px', lg:'20px'},
                    fontFamily: 'InterRegular',
                    fontWeight: 300,
                  }}>
                    Total Amount:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 700, 
                      color: '#000',
                      fontSize: {sm:'20px',md:'16px',lg:'20px'},
                      fontFamily: 'InterSemiBold',
                    }}>
                      {isNextInvoice? `$${billingOverview.billing_info[0].price}` : '$0.00'}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#000',
                      fontSize: {sm:'12px',md:'10px',lg:'12px'},
                      fontWeight: 300,
                      fontFamily: 'InterRegular',
                    }}>
                      USD
                    </Typography>
                  </Box>
                </Box>

                {/* Row 2: Currency + Due Date */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" sx={{ 
                  color: '#adadad',
                  fontSize: {sm:'10px', md:'9px', lg:'10px'},
                  mb: 2
                }}>
                 Payment Due: {isNextInvoice
                  ? formatOneMonthLater(billingOverview.billing_info[0].date)
                  : ""}
                </Typography>
                </Box>
                </Box>
                ) : (   
                   <Box sx={{ 
                    border: '0.7px solid #E9ECEF',
                    borderRadius: '10px',
                    textAlign: 'center',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: {sm:'340px', md:'216px', lg:'340px'},
                    height: '75px',
                    padding: '12px 16px',
                    display: 'flex',
                    mb: {sm:6, md:5, lg:7}
                  }}>
                  <Typography variant="body2" sx={{ color: '#6C757D', fontSize: '14px' }}>
                    No invoice is available
                  </Typography>
                </Box>
                )}

              <Box sx={{ 
                backgroundColor: '#fff',
                width: '100%',
                mb: 3,
                display: 'flex',
                justifyContent: 'center',
              }}>            
                <Button
                sx={{
                  backgroundColor: isNextInvoice? '#4A6CCE' : '#ccc',
                  borderRadius: '10px',
                  textTransform: 'none',
                  width: {sm:'100%', md:'100%', lg:'90%'},
                  color: '#fff',
                  mt: {xs: 2},
                  fontWeight: 700,
                  fontSize: '12px !important',
                  fontFamily: 'InterRegular',
                  height: '35px',
                  cursor: isNextInvoice ? 'pointer' : 'not-allowed',
                  '&:hover': {
                     backgroundColor: isNextInvoice ? '#4A6CCE' : '#ccc',
                  }
                }}
                fullWidth
                onClick={() => {
                if (isNextInvoice) {
                  handleProceedToPay(billingOverview.billing_info[0].stripe_invoice_url);
                }
                }}
              >
                Proceed to Pay
              </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* User Overview Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: {sm: '235px', md:'210px', lg:'235px'},
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E0E0E0'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700,
                  color: '#000',
                  fontSize: '18px',
                  fontFamily: 'InterMedium',
                }}>
                  User Overview
                </Typography>
              </Box>
              
              <Box sx={{ 
                width: {xs:'100%', md:'216px', lg:'340px'},
                backgroundColor: '#fff',
                borderRadius: 2,
                p: 2,
                mb: 3,
                mt: 2.5,
                border: '1px solid #E9ECEF'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                <Typography variant="h3" sx={{ 
                  fontWeight: 600,
                  fontFamily: 'InterSemiBold', 
                  mb: 0.5,
                  color: '#212529',
                  fontSize: {sm: '20px', md:'16px', lg:'20px'},
                }}>
                  {`$${billingOverview.company_info.cost_per_employee}`}
                </Typography>
                <Typography variant="body2" sx={{ 
                  mb: 1,
                  color: '#000',
                  fontSize: {sm:'12px', md:'10px', lg:'12px'},
                  fontWeight: 300,
                  fontFamily: 'InterRegular',
                }}>
                  USD
                </Typography>
                </Box>
                {billingOverview.company_info.account_type == "standard" &&
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                  <Typography variant="body2" sx={{ 
                    color: '#6C757D',
                    fontSize: {sm:'12px', md:'10px', lg:'12px'},
                    fontWeight: 300,
                    fontFamily: 'InterRegular',}}>
                    PER USER
                  </Typography>
                   <Typography variant="body2" sx={{ 
                      color: '#6C757D',
                      fontSize: {sm:'12px', md:'10px', lg:'12px'},
                      fontWeight: 300,
                      fontFamily: 'InterRegular',
                    }}>
                      INVITED: {billingOverview.user_info.invited_users}
                    </Typography>
                  </Box>
                }
              </Box>

              <Box sx={{ 
                backgroundColor: '#fff',
                width: {xs: '100%', md:'216px', lg:'340px'},
                borderRadius: 2,
                display: 'flex',
                justifyContent: 'center',
                p: 2,
                border: '1px solid #E9ECEF'
              }}>
                <Grid container spacing={1} sx={{display:'flex', justifyContent:'space-between'}}>
                   <Grid item xs={12} sx={{display: 'flex', justifyContent: 'space-between'}}>
                    <Typography variant="body2" sx={{ 
                      color: '#000',
                      fontSize: {sm: '16px', md:'14px', lg:'16px'},
                      fontWeight: 500,
                      fontFamily: 'InterMedium',
                    }}>
                      ACTIVE: {billingOverview.user_info.active_users}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#000',
                      fontSize: {sm: '16px', md:'14px', lg:'16px'},
                      fontWeight: 500,
                      fontFamily: 'InterMedium',
                    }}>
                      PAID: {billingOverview.user_info.paid_users}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sx={{display: 'flex', justifyContent: 'space-between'}}>
                    <Typography variant="body2" sx={{ 
                      ccolor: '#000',
                      fontSize: {sm: '16px', md:'14px', lg:'16px'},
                      fontWeight: 500,
                      fontFamily: 'InterMedium',
                    }}>
                      EXPIRED: {billingOverview.user_info.expired_users}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      ccolor: '#000',
                      fontSize: {sm: '16px', md:'14px', lg:'16px'},
                      fontWeight: 500,
                      fontFamily: 'InterMedium',
                    }}>
                      UNPAID: {billingOverview.user_info.unpaid_users}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Billing Table */}
      <Card sx={{ 
        borderRadius: '20px', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        backgroundColor: '#FFFFFF',
        border: '1px solid #F0F0F0'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 700,
              color: '#212529',
              fontSize: '20px',
              fontFamily: 'InterBold',
            }}>
              Billing
            </Typography>
            <IconButton size="small" sx={{ color: '#6C757D' }}>
              <MoreHorizIcon />
            </IconButton>
          </Box>
          
          <TableContainer component={Paper} sx={{ 
            boxShadow: 'none',
            borderRadius: 2,
            border: '1px solid #E9ECEF',
            backgroundColor: '#FFFFFF'
          }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ 
                  backgroundColor: '#F8F9FA',
                  '& th': {
                    borderBottom: '2px solid #E9ECEF'
                  }
                }}>
                  {tableHeaders.map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        fontWeight: 600,
                        color: '#000',
                        fontSize: '14px',
                        py: 2.5,
                        px: 3,
                        fontFamily: 'InterSemiBold',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedInvoices.length > 0 ? (
                paginatedInvoices.map((row, index) => (
                  <TableRow 
                    key={index} 
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: '#F8F9FA',
                        transition: 'background-color 0.2s ease'
                      },
                      '&:not(:last-child)': {
                        borderBottom: '1px solid #E9ECEF'
                      },
                      backgroundColor: '#FFFFFF' 
                    }}
                  >
                    <TableCell sx={{ 
                      py: 2.5, 
                      px: 3,
                      borderBottom: '1px solid #E9ECEF'
                    }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 400,
                        color: '#000',
                        fontSize: '14px',
                        fontFamily: 'InterRegular',
                      }}>
                        {row.stripe_invoice_id || row.invoice_id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ 
                      py: 2.5, 
                      px: 3,
                      borderBottom: '1px solid #E9ECEF'
                    }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 400,
                        color: '#000',
                        fontSize: '14px',
                        fontFamily: 'InterRegular',
                      }}>
                        {row.date instanceof Date 
                          ? row.date.toLocaleDateString() 
                          : new Date(row.date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ 
                      py: 2.5, 
                      px: 3,
                      borderBottom: '1px solid #E9ECEF'
                    }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 400,
                        color: '#000',
                        fontSize: '14px',
                        fontFamily: 'InterRegular',
                      }}>
                        ${row.price}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ 
                      py: 2.5, 
                      px: 3,
                      borderBottom: '1px solid #E9ECEF'
                    }}>
                      <Chip
                        label= {statusLabels[row.payment_status] || row.payment_status}
                        sx={{
                          backgroundColor: getStatusBackground(row.payment_status),
                          color: getStatusColor(row.payment_status),
                          fontWeight: 400,
                          borderRadius: '20px',
                          border: row.payment_status === 'Refunded' ? '1px solid #000' : `1px solid ${getStatusColor(row.payment_status)}`,
                          fontSize: '14px',
                          height: '28px',
                          fontFamily: 'InterRegular',
                          minWidth: '80px',
                          '& .MuiChip-label': {
                            px: 2,
                            py: 0.5
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ 
                      py: 2.5, 
                      px: 3,
                      borderBottom: '1px solid #E9ECEF'
                    }}>
                      <Link
                        href= {
                          row.stripe_invoice_pdf_url && row.payment_status !== 'expired'
                          ? row.stripe_invoice_pdf_url
                          : undefined
                        }
                        onClick={(e) => {
                          if (!row.stripe_invoice_pdf_url || row.payment_status === 'expired') {
                            e.preventDefault()
                          }
                        }}
                        sx={{
                          color: row.stripe_invoice_pdf_url && row.payment_status !== 'expired' ? '#000' : '#aaa',
                          textDecoration:
                          row.stripe_invoice_pdf_url && row.payment_status !== 'expired'
                            ? 'underline !important'
                            : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          fontFamily: 'InterRegular',
                          gap: 1,
                          fontSize: '14px',
                          fontWeight: 400,
                          cursor:
                          row.stripe_invoice_pdf_url && row.payment_status !== 'expired'
                            ? 'pointer'
                            : 'default',
                        '&:hover': {
                          textDecoration:
                            row.stripe_invoice_pdf_url && row.payment_status !== 'expired'
                              ? 'underline !important'
                              : 'none',
                        },
                        }}
                      >
                        Download Invoice
                      </Link>
                    </TableCell>
                  </TableRow>
                ))):(
                  <TableRow>
                  <TableCell colSpan={tableHeaders.length} sx={{ textAlign: 'center', py: 5 }}>
                    <Typography variant="body2" sx={{ color: '#6C757D', fontSize: '14px' }}>
                      No invoice is available
                    </Typography>
                  </TableCell>
                </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
            <Typography variant="body2" sx={{ 
              color: '#6C757D',
              fontSize: '0.875rem',
              fontWeight: 500
               }}>
              {billingOverview.billing_info.length > 0
                ? `Showing ${(page - 1) * rowsPerPage + 1} - ${Math.min(page * rowsPerPage, billingOverview.billing_info.length)} of ${billingOverview.billing_info.length}`
                : ''}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              shape="rounded"
              color="primary"
              size="large"
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: '50%',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  minWidth: '40px',
                  border: '1px solid #E9ECEF',
                  height: '40px',
                  margin: '0 4px',
                  '&:hover': {
                    backgroundColor: '#E9ECEF',
                  }
                },
                '& .MuiPaginationItem-root.Mui-selected': {
                  backgroundColor: '#426BF0',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  '&:hover': {
                    backgroundColor: '#3B5BDB',
                  }
                },
                '& .MuiPaginationItem-root.MuiPaginationItem-previousNext': {
                  backgroundColor: '#fff',
                  border: '1px solid #E9ECEF',
                  '&:hover': {
                    backgroundColor: '#E9ECEF',
                  }
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Footer */}
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Typography variant="body2" sx={{ 
          color: '#2F2F2F',
          fontSize: '12px',
          lineHeight: 1.6,
          fontWeight: 400,
          fontFamily: 'InterRegular', 
        }}>
          If you would like to terminate your Priwall Subscription, please contact us at{' '}
          <Link 
            href="mailto:support@meprism.com" 
            sx={{ 
              color: '#000 !important', 
              textDecoration: 'none',
              fontWeight: 400,
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            support@meprism.com
          </Link>
          .
        </Typography>
      </Box>
    </Container>
  );
}

